import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const sendCode = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/verify/send", {});
      if (data?.alreadyVerified) { await refreshUser(); toast.success("Your email is already verified"); navigate("/profile"); return; }
      toast.success("Code sent — check your inbox");
      setCooldown(45);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setResending(false); }
  };

  useEffect(() => {
    if (user?.email_verified) navigate("/profile");
  }, []); // eslint-disable-line

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setVerifying(true);
    try {
      await api.post("/auth/verify/confirm", { code });
      await refreshUser();
      toast.success("Email verified — you're all set!");
      navigate("/profile");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 py-8">
      <div className="mx-auto max-w-md">
        <button onClick={() => navigate(-1)} data-testid="verify-back-btn" className="mb-6 flex h-10 w-10 items-center justify-center rounded-full text-emerald-900 hover:bg-mint">
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-mint text-emerald-800">
          <MailCheck className="h-8 w-8" />
        </div>

        <h1 className="font-serif text-4xl font-bold text-emerald-950">Verify your email</h1>
        <p className="mt-3 leading-relaxed text-slate-500">
          We emailed a 6-digit code to <b className="text-emerald-900">{user?.email}</b>. Enter it below to confirm your email address.
        </p>

        <form onSubmit={verify} className="mt-8" data-testid="verify-email-form">
          <label className="mb-2 block text-sm font-semibold text-emerald-950">6-digit code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoFocus
            placeholder="123456"
            data-testid="verify-code-input"
            className="w-full rounded-2xl border border-emerald-900/15 bg-white px-5 py-4 text-center text-2xl font-bold tracking-[0.4em] text-emerald-950 outline-none placeholder:text-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
          />
          <Button type="submit" disabled={verifying || code.length !== 6} data-testid="verify-submit-btn" className="mt-5 w-full rounded-full bg-emerald-800 py-6 text-base font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
            {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify email"}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-slate-500">
          Didn't get it?{" "}
          <button type="button" onClick={sendCode} disabled={resending || cooldown > 0} data-testid="verify-resend-btn" className="font-bold text-emerald-800 hover:underline disabled:opacity-50">
            {resending ? "Sending…" : cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
          </button>
        </p>

        <div className="mt-12 flex justify-center opacity-60"><Logo size="sm" /></div>
      </div>
    </div>
  );
}
